import React, { useEffect, useRef, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import { CustomToggleProps, MultiSelectOptionProps, MultiSelectProps } from "./labeled-multi-select.props";
import { IconCheckSquare, IconChevronDown, IconSquare } from "../icons";
import "./labeled-multi-select.scss";

const CustomToggle = React.forwardRef(({ children, onClick }: CustomToggleProps, ref: React.Ref<HTMLButtonElement>) => (
  <Button
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      if (onClick) onClick(e);
    }}
    data-testid="option-multi-selector-trigger"
    variant="outline-secondary"
    className="mpng-timeframe-button"
    arial-label="Multi option selector"
  >
    {children}
    <IconChevronDown className="mpng-timeframe-button-icon" />
  </Button>
));
//making because of code smell
const toSorted = (data: MultiSelectOptionProps[]) => {
  return data.sort((a: MultiSelectOptionProps, b: MultiSelectOptionProps) => a.name.localeCompare(b.name));
};
const LabeledMultiSelect = ({
  label,
  onOptionSelected,
  className,
  testid,
  style,
  separator,
  selected = [],
  options = [],
  autoCloseOnSelect = true,
  displayApplyButton = false
}: MultiSelectProps) => {
  const [optionsValues, setOptionsValues] = useState<JSX.Element[]>([]);
  const [selectedItem, setSelectedItem] = useState(selected);
  const dropdownRef = useRef(null);

  const getSeparator = separator ? ` ${separator} ` : `, `;

  const selectedOptions = toSorted(selected)
    .map((item: MultiSelectOptionProps) => item.name)
    .toString()
    .replace(/,/g, getSeparator);

  const innerHtml = (
    <>
      <div {...(testid ? { "data-testid": testid } : {})} className="multi-range">
        <span className="multi-range-window">{label}</span>
        <br />
        <b>
          {selectedOptions && <span className="multi-range-label">{selectedOptions}</span>}
        </b>
      </div>
    </>
  );

  const toggleOption = (id: string | number, isSelected: boolean) => {
    let newSelected = [...selectedItem];
    if (isSelected) {
      newSelected = newSelected.filter((item) => item.id !== id);
    } else {
      newSelected = [...newSelected, ...options.filter((item) => item.id === id)];
    }
    setSelectedItem(newSelected);
    if (!displayApplyButton) {
      onOptionSelected(newSelected);
    }
  };

  const handleApplyChanges = () => {
    onOptionSelected(selectedItem);
    dropdownRef.current.click();
  };

  useEffect(() => {
    setSelectedItem(selected);
  }, [selected]);

  useEffect(() => {
    const items = options.map((item) => {
      const isSelected = selectedItem.map((item: MultiSelectOptionProps) => item.id).indexOf(item.id) > -1;
      return (
        <Dropdown.Item
          key={item.id}
          eventKey={item.id}
          data-testid={`mpng-multiselect-option-${item.name}`}
          onClick={(e) => {
            toggleOption(item.id, isSelected);
            e.preventDefault();
          }}
        >
          <span>
            {isSelected ? <IconCheckSquare /> : <IconSquare />} {item.name}
          </span>
        </Dropdown.Item>
      );
    });
    setOptionsValues(items);
  }, [selectedItem, options]);

  return (
    <div
      style={{ ...style }}
      className={`${className ?? ""} mpng-labeled-multi-selector`}
      data-testid="mpng-labeled-multi-selector"
    >
      <Dropdown
        tabIndex={0}
        ref={dropdownRef}
        {...(autoCloseOnSelect && !displayApplyButton ? {} : { autoClose: "outside" })}
      >
        <Dropdown.Toggle
          as={CustomToggle}
          variant="outline-secondary"
          className="mpng-timeframe-button mpng-header-button"
          aria-label="Select options"
        >
          {innerHtml}
        </Dropdown.Toggle>
        <Dropdown.Menu role="listbox" className="listbox">
          {optionsValues}
          {displayApplyButton && (
            <Dropdown.Item
              className="dropdown-apply-button"
              data-testid="mpng-multiselect-apply-button"
              onClick={handleApplyChanges}
            >
              <span>Apply Changes</span>
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default LabeledMultiSelect;
